import BaseComponent from 'scripts/BaseComponent.js';
import axios from 'axios';
import gsap from 'gsap';
import Observer from 'gsap/dist/Observer';
import UnderlayHelper from '../../global/underlay-helper';
import { AccessibilityHelper } from '../../../scripts/accessibility/accessibilityHelper';

export default class QuickShop extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      closeBtn: '.js-quickShopCloseBtn',
      infoToggle: '.js-quickShopInfoToggle',
      imagesContainer: '.js-quickShopProductImages',
      optionsContainer: '.js-quickShopProductOptions',
      formContainer: '.js-quickShopProductForm',
      productJsonScript: '.js-quickShopProductJson',
      infoDrawer: '.js-quickShopInfoDrawer',
    };

    this.dom = {
      $component: component,
      $closeBtn: this.$get(this.refs.closeBtn),
      $infoToggle: this.$getAll(this.refs.infoToggle),
      $imagesContainer: this.$get(this.refs.imagesContainer),
      $optionsContainer: this.$get(this.refs.optionsContainer),
      $formContainer: this.$get(this.refs.formContainer),
      $productJsonScript: this.$get(this.refs.productJsonScript),
      $infoDrawer: this.$get(this.refs.infoDrawer),
    };

    this.state = {
      openClass: 'is-open',
      showClass: 'show',
      infoExpandedClass: 'info-expanded',
      bodyFixedClass: 'is-fixed',
      desktopBreakpoint: '(min-width: 768px)',
      animationDuration: 500,
      openQuickShopEvent: 'openquickshop',
      closeQuickShopEvent: 'closequickshop',
      mountQuickShopEvent: 'mountquickshop',
      underlayHelper: new UnderlayHelper({ overNav: true }),
      accessibilityHelper: new AccessibilityHelper(),
      focusTrap: null,
    };

    this.mount();
  }

  mount() {
    const { dom, state } = this;
    super.mount();
    this.initObserver();

    this.setOnCustomEvent(state.openQuickShopEvent, this.setQuickShop);
    this.setOnCustomEvent(state.closeQuickShopEvent, this.closeQuickShop);
    this.setClick(dom.$closeBtn, this.closeQuickShop);
    this.setClick(dom.$infoToggle, this.infoToggle);
    state.underlayHelper.addClickListener(this.closeQuickShop.bind(this));
    state.focusTrap = state.accessibilityHelper.buildFocusTrap([dom.$component]);
  }

  async setQuickShop({ detail }) {
    try {
      const page = await this.fetchQuickShopTemplate(detail);
      this.parseQuickShopTemplate(page);
      this.openQuickShop();
    } catch (e) {
      console.error('Unable to open quick shop', e);
    }
  }

  async fetchQuickShopTemplate({
    id, handle, sku, title,
  }) {
    try {
      const url = `/products/${handle}?variant=${id}&view=quick-shop`;
      const { data } = await axios.get(url);
      const parser = new DOMParser();
      const page = parser.parseFromString(data, 'text/html');

      window.dataLayer.push({
        event: 'quick_shop_open',
        product_title: title,
        variant_sku: sku,
      });

      return page;
    } catch (e) {
      console.error('Error fetching quick shop data', e);
      throw e;
    }
  }

  parseQuickShopTemplate(page) {
    const { dom, refs, state } = this;

    const imagesContainer = page.querySelector(refs.imagesContainer);
    dom.$imagesContainer.innerHTML = imagesContainer.innerHTML;

    const optionsContainer = page.querySelector(refs.optionsContainer);
    dom.$optionsContainer.innerHTML = optionsContainer.innerHTML;

    const formContainer = page.querySelector(refs.formContainer);
    dom.$formContainer.innerHTML = formContainer.innerHTML;

    const productJsonScript = page.querySelector(refs.productJsonScript);
    dom.$productJsonScript.innerHTML = productJsonScript.innerHTML;

    this.dispatch(state.mountQuickShopEvent);
  }

  openQuickShop() {
    const { dom, state } = this;

    dom.$component.classList.add(state.openClass);
    setTimeout(() => {
      dom.$component.classList.add(state.showClass);
    }, 0);

    if (!window.matchMedia(state.desktopBreakpoint).matches) {
      document.body.classList.add(state.bodyFixedClass);
    }

    dom.$component.setAttribute('aria-hidden', 'false');
    dom.$component.setAttribute('aria-expanded', 'true');
    state.underlayHelper.open();
    state.focusTrap.set();
  }

  closeQuickShop() {
    const { dom, state } = this;

    dom.$component.classList.remove(state.showClass);
    setTimeout(() => {
      dom.$component.classList.remove(state.openClass);
      dom.$component.classList.remove(state.infoExpandedClass);
    }, state.animationDuration);

    document.body.classList.remove(state.bodyFixedClass);
    dom.$component.setAttribute('aria-hidden', 'true');
    dom.$component.setAttribute('aria-expanded', 'false');
    state.underlayHelper.close();
    state.focusTrap.unset();
  }

  initObserver() {
    const { dom, state } = this;
    if (window.matchMedia(state.desktopBreakpoint).matches) return;

    gsap.registerPlugin(Observer);

    Observer.create({
      type: 'touch',
      target: dom.$infoDrawer,
      dragMinimum: 50,
      onDragEnd: this.observerDragEnd.bind(this),
    });
  }

  observerDragEnd({ deltaY, velocityY }) {
    const { dom, state } = this;
    const velocity = Math.abs(velocityY);
    if (velocity < 600) return;

    if (deltaY < 0) {
      dom.$component.classList.add(state.infoExpandedClass);
    } else {
      dom.$component.classList.remove(state.infoExpandedClass);
    }
  }

  infoToggle() {
    const { dom, state } = this;
    dom.$component.classList.toggle(state.infoExpandedClass);
  }
}
